
import { SavedJob, JobSearchResult } from '@/types/jobSearch';
import { supabase } from '@/integrations/supabase/client';
import { logErrorToast } from '@/utils/toastLogger';
import { JOB_SEARCH_RESULT_FIELDS_SELECT } from '../constants/jobServiceConstants';
import { Json } from '@/integrations/supabase/types'; // Ensure Json type is imported if used for raw_data

/**
 * Fetches saved jobs for a user from Supabase
 */
export const fetchSavedJobs = async (userId: string): Promise<SavedJob[]> => {
  if (!userId) {
    return [];
  }
  
  try {
    const { data, error: fetchError } = await supabase
      .from('saved_jobs')
      .select(`
        id,
        user_id,
        job_search_result_id,
        notes,
        created_at,
        updated_at,
        job_search_results:job_search_result_id (
          ${JOB_SEARCH_RESULT_FIELDS_SELECT}
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (fetchError) {
      throw fetchError;
    }
    
    const transformedData = (data || [])
      .filter(sj => sj.job_search_results && typeof sj.job_search_results === 'object' && !('error' in sj.job_search_results))
      .map(sj => {
        const dbResult = sj.job_search_results as JobSearchResult;
        const originalJobData = dbResult.raw_data as JobSearchResult | null;

        // Robust URL determination
        // dbResult.job_url is the primary canonical URL from the job_search_results table.
        // dbResult.url is an alias to dbResult.job_url due to JOB_SEARCH_RESULT_FIELDS_SELECT.
        const determinedJobUrl = dbResult.job_url || // Canonical URL from DB
                               originalJobData?.job_url || // Fallback to raw_data.job_url
                               originalJobData?.url; // Fallback to raw_data.url

        // Score determination
        let calculatedScore: number | null = null;
        if (dbResult.score !== null && dbResult.score !== undefined) {
          calculatedScore = dbResult.score;
        } else if (originalJobData?.score !== null && originalJobData?.score !== undefined) {
          calculatedScore = Number(originalJobData.score); // Ensure it's a number
        }

        // Source determination
        const determinedSource = dbResult.source || originalJobData?.source;
        
        const finalJobSearchResult: JobSearchResult = {
          ...(originalJobData || {}), // Spread raw_data first as a base

          // Override with values directly from dbResult (canonical) or determined fallbacks
          id: dbResult.id,
          title: dbResult.title || (dbResult as any).job_title,
          company_name: dbResult.company_name,
          location: dbResult.location,
          job_url: determinedJobUrl, // Use the robustly determined job URL
          url: determinedJobUrl, // Keep url consistent with job_url for now, based on select alias
          source: determinedSource, // Use determined source
          snippet: dbResult.snippet,
          posted_date: dbResult.posted_date,
          score: calculatedScore, // Use calculated score
          created_at: dbResult.created_at,
          updated_at: dbResult.updated_at,
          status: dbResult.status,
          provider_id: dbResult.provider_id,
          relevance_explanation: dbResult.relevance_explanation,
          
          description: originalJobData?.description, // Description primarily from raw_data
          employment_type: originalJobData?.employment_type,
          experience_level: originalJobData?.experience_level,
          salary_range: originalJobData?.salary_range,
          benefits: originalJobData?.benefits,
          qualification_details: originalJobData?.qualification_details,
          responsibility_details: originalJobData?.responsibility_details,
          
          raw_data: dbResult.raw_data || originalJobData, 
        };
        
        return {
          ...sj,
          job_search_results: finalJobSearchResult,
        };
      });

    return transformedData as SavedJob[];
  } catch (e: any) {
    const errorMessage = e.message || 'Could not fetch saved jobs.';
    logErrorToast(errorMessage, e);
    return [];
  }
};
